export type PhmsAuditActivityPerspective = "inmate" | "hearing";

export interface PhmsAuditActivityEntry {
  id: number;
  occurredUtc: string;
  userId: number | null;
  actorDisplayName: string;
  eventType: string;
  entityName: string;
  entityId: number | null;
  operation: string;
  summary: string;
  requestPath: string | null;
  outcome: string | null;
  changedPaths: readonly string[];
}

export interface PhmsAuditActivityDisplayError {
  title: string;
  messages: readonly string[];
  statusCode?: number;
}

export function mapAuditActivityError(error: unknown): PhmsAuditActivityDisplayError {
  const statusCode = typeof error === "object" && error !== null && "status" in error
    ? Number((error as { status?: unknown }).status)
    : undefined;

  if (statusCode === 403) {
    return {
      title: "Audit activity unavailable",
      messages: ["You do not have permission to view audit activity for this record."],
      statusCode,
    };
  }

  if (statusCode === 404) {
    return {
      title: "Audit activity not found",
      messages: ["No audit activity could be found for this record."],
      statusCode,
    };
  }

  return {
    title: "Audit activity could not be loaded",
    messages: ["Try refreshing the page. If this continues, contact support."],
    statusCode: Number.isFinite(statusCode) ? statusCode : undefined,
  };
}

export function formatAuditActivityWhen(value: string | null | undefined): string {
  if (!value) {
    return "Unknown time";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "Unknown time";
  }

  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

export function formatAuditActivityCode(value: string | null | undefined): string {
  const normalized = (value ?? "").trim();
  if (!normalized) {
    return "";
  }

  return normalized
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[._\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(" ");
}
